import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  Renderer2,
  ViewChild,
} from "@angular/core";
import ApexCharts from "apexcharts";
import { Datepicker } from "flowbite-datepicker";
import moment, { Moment } from "moment-timezone";
import Papa from "papaparse";
import {
  catchError,
  debounceTime,
  EMPTY,
  finalize,
  fromEvent,
  Subject,
  Subscription,
} from "rxjs";
import {
  currentDate,
  currentDatePast,
  startDateEtWaterYear,
  startDateWaterYearPast,
} from "../../../field-claim/map/util/utliWaterYear";
import { cropDict, escapeCSV, formatNumber } from "../../utils/data-util";
import { GraphPopoverInstructionComponent } from "../../utils/graph-popover-instruction/graph-popover-instruction.component";
import { InfoPopoverComponent } from "../../utils/info-popover/info-popover.component";
import { DataService } from "../../../services/api/data/data.service";
import { ThemeService } from "../../../services/theme/theme.service";

import {
  getCropBarChartOptions,
  updateCropBarChartOptionsForTextMode,
} from "./config/crop-summary-barchart-config";
import {
  cropColorsLegend,
  cropColorsLegendTopFive,
} from "./data/crop-colors-dict";
import {
  getSummaryChartOptions,
  updateSummaryChartOptionsForTextMode,
} from "./config/piechart-config";
import {
  getUpdateTimeseriesChartConfig,
  timeseriesChartConfig,
} from "./config/timeseries-chart-config";

interface DashboardTimeseriesPoint {
  et_date: string;
  eta: number;
  ceta: number;
  prcp: number;
  cprcp: number;
}

interface DashboardCropSummary {
  crop: string;
  acres: number;
  fieldsCount: number;
  cumulativeAvgEtAcFtPerAc: number;
}

interface DashboardCropSummaryByDate {
  date: string;
  crops: DashboardCropSummary[];
}

interface DashboardCropSummaryAccumulator {
  acres: number;
  fieldsCount: number;
  cumulativeEtAcres: number;
}

interface ManagerDashboardJson {
  title: string;
  updatedAt?: string;
  updateAt?: string;
  usersCount: number;
  claimedFieldsCount: number;
  totalFieldsCount: number;
  totalClaimedAcres: number;
  totalAcres: number;
  cumulativeAvgEtAcFtPerAc: number;
  cumulativeAvgPrecipIn: number;
  estimatedCumulativeTotalConsumptionTaf: number;
  totalGroundWaterMed?: number | null;
  totalSurfaceWaterAppliedMed?: number | null;
  timeseries: DashboardTimeseriesPoint[];
  cropSummariesByDate: DashboardCropSummaryByDate[];
}

import { CommonModule, NgTemplateOutlet } from "@angular/common";
import { AuthService } from "../../../services/api/auth/auth.service";
import { LoadingService } from "../../../services/loading/loading";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "app-statistics-manager-dashboard",
  standalone: true,
  imports: [
    GraphPopoverInstructionComponent,
    InfoPopoverComponent,
    CommonModule,
    NgTemplateOutlet,
  ],
  templateUrl: "./statistics-manager-dashboard.component.html",
  styleUrl: "./statistics-manager-dashboard.component.scss",
  host: {
    class: "flex flex-col h-full overflow-hidden",
  },
})
export class StatisticsManagerDashboardComponent implements OnDestroy {
  chartOptions: any = timeseriesChartConfig;

  @ViewChild("chart") chartElement!: ElementRef;
  @ViewChild("summaryChart") summaryChartElement!: ElementRef;
  @ViewChild("waterSourcesChart") waterSourcesChartElement?: ElementRef;
  @ViewChild("cropBarChart") cropBarChartElement?: ElementRef;
  @ViewChild("dropdownButton") dropdownButton!: ElementRef;
  @ViewChild("dropdownMenu") dropdownMenu!: ElementRef;
  @ViewChild("datepickerInput")
  datepickerInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild("growerCircle") growerCircleRef!: ElementRef<HTMLElement>;
  @ViewChild("etCircle") etCircleRef!: ElementRef<HTMLElement>;
  @ViewChild("precipCircle") precipCircleRef!: ElementRef<HTMLElement>;
  @ViewChild("consumptionCircle")
  consumptionCircleRef!: ElementRef<HTMLElement>;
  @ViewChild("cardsContainer") cardsContainerRef!: ElementRef<HTMLElement>;

  private subscriptions = new Subscription();
  isOpen = false;
  title?: string;
  clickListener?: () => void;
  chart?: any;
  summaryChart?: any;
  waterSourcesChart?: ApexCharts;
  cropBarChart?: any;
  mode = localStorage.getItem("theme");
  timeRange = "All Water Year";
  timeRangeFilter: { [key: string]: number } = {
    "Last 7 days": 7,
    "Last 15 days": 15,
    "Last 30 days": 30,
    "Last 60 days": 60,
    "All Water Year": 367,
  };
  cropPairs: { [key: string]: string };
  totalETPrecipCumulativeET: DashboardTimeseriesPoint[] = [];
  growerCount = 0;
  claimedFieldsCount = 0;
  totalFieldsCount = 0;
  totalClaimedAcres = 0;
  totalAcres = 0;
  totalETInInch = 0;
  totalETInFt = 0;
  totalPrcpInInch = 0;
  claimedRate = 0;
  displayFieldsStatus = false;
  showDashboardBanner = true;
  dashboardUpdatedDate = "";
  projectedTotalConsumption = 0;
  circleSize: number | null = null;
  minDateStr = startDateEtWaterYear().format("YYYY-MM-DD");
  maxDateStr = currentDate().format("YYYY-MM-DD");
  currentDate: Moment;
  dateStart: Moment;
  cropCetaPerennialDict: { [key: string]: [number, number, number, string] } =
    {};
  sortedCropCetaPerennialList: [string, [number, number, number, string]][] =
    [];
  cropTypesCount = 0;
  private dashboardData!: ManagerDashboardJson;
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private circleResizeSubject = new Subject<void>();
  private circleResizeObserver?: ResizeObserver;

  constructor(
    private renderer: Renderer2,
    private dataService: DataService,
    private themeService: ThemeService,
    private loadingService: LoadingService,
  ) {
    this.cropPairs = cropDict;
    if (this.authService.internalModeValue === "demo") {
      this.minDateStr = startDateWaterYearPast().format("YYYY-MM-DD");
      this.maxDateStr = currentDatePast().format("YYYY-MM-DD");
      this.currentDate = currentDatePast();
      this.dateStart = startDateWaterYearPast();
    } else {
      this.currentDate = currentDate();
      this.dateStart = startDateEtWaterYear();
    }
  }

  convertToCsv(dataArray: DashboardTimeseriesPoint[]): string {
    const headers = ["et_date", "eta", "ceta", "prcp", "cprcp"];
    const heardersAlias = [
      "Date",
      "Evapotranspiration (in)",
      "Cumulative Evapotranspiration (in)",
      "Precipitation (in)",
      "Cumulative Precipitation (in)",
    ];
    const headerRow = heardersAlias.join(",");
    const rows = dataArray.map((item) =>
      headers
        .map((field) => {
          if (item.et_date > this.currentDate.format("YYYY-MM-DD")) {
            return null;
          }
          const value = item[field as keyof DashboardTimeseriesPoint];
          return (
              field === "eta" ||
                field === "ceta" ||
                field === "prcp" ||
                field === "cprcp"
            ) ?
              formatNumber(value)
            : value;
        })
        .map(escapeCSV)
        .join(","),
    );
    return [headerRow, ...rows].join("\n");
  }

  downloadET(): void {
    const csvData = this.convertToCsv(this.totalETPrecipCumulativeET);
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = this.title + "_daily.csv";
    anchor.click();
    window.URL.revokeObjectURL(url);
    anchor.remove();
  }

  downloadCropET(): void {
    const headers = [
      "Crop",
      "Acres",
      "Fields Count",
      "Cumulative Avg ET (ac-ft/ac)",
    ];
    const data = this.sortedCropCetaPerennialList.map((item) => [
      item[0],
      item[1][1],
      item[1][2],
      item[1][0],
    ]);
    const csv = Papa.unparse({ fields: headers, data });
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const selectedDate =
      this.datepickerInputRef.nativeElement.value || this.maxDateStr;
    a.download = `Crop_Cumulative_Avg_ET_${selectedDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  onTimeFilterChange(range: string) {
    this.timeRange = range;
    this.closeDropdown();
    this.updateCharts();
  }

  dismissDashboardBanner(): void {
    this.showDashboardBanner = false;
  }

  private formatDashboardUpdatedDate(updatedAt?: string): string {
    if (!updatedAt) {
      return "";
    }

    const parsedDate = moment(updatedAt);
    return parsedDate.isValid() ?
        parsedDate.format("YYYY-MM-DD hh:mm:ss A")
      : updatedAt;
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  closeDropdown() {
    this.isOpen = false;
  }

  ngOnInit(): void {
    this.clickListener = this.renderer.listen(
      "document",
      "click",
      (event: Event) => {
        const targetElement = event.target as HTMLElement;
        if (
          !this.dropdownButton.nativeElement.contains(targetElement) &&
          !this.dropdownMenu.nativeElement.contains(targetElement)
        ) {
          this.closeDropdown();
        }
      },
    );
    this.subscriptions.add(
      this.themeService.darkMode$.subscribe((isDarkMode) => {
        this.mode = isDarkMode === true ? "dark" : "light";
        if (this.summaryChart) {
          this.summaryChart.updateOptions(
            updateSummaryChartOptionsForTextMode(this.mode),
          );
        }
        if (this.waterSourcesChart) {
          this.waterSourcesChart.updateOptions({
            chart: {
              foreColor: this.mode === "dark" ? "#f1f5f9" : "#374151",
            },
            legend: {
              labels: {
                colors: this.mode === "dark" ? "#f1f5f9" : "#374151",
              },
            },
            plotOptions: {
              pie: {
                donut: {
                  labels: {
                    name: {
                      color: this.mode === "dark" ? "#f1f5f9" : "#374151",
                    },
                    value: {
                      color: this.mode === "dark" ? "#f1f5f9" : "#111827",
                    },
                    total: {
                      color: this.mode === "dark" ? "#f1f5f9" : "#111827",
                    },
                  },
                },
              },
            },
          });
        }
        if (this.cropBarChart) {
          this.cropBarChart.updateOptions(
            updateCropBarChartOptionsForTextMode(this.mode),
          );
        }
      }),
    );
  }

  ngAfterViewInit() {
    this.fetchManagerStatisticsSummary();
    const interval = setInterval(() => {
      const input = document.getElementById(
        "datepicker-cropSummary",
      ) as HTMLInputElement;
      if (input) {
        const existing = (input as any).datepicker;
        if (existing) {
          existing.destroy();
        }
        new Datepicker(input, {
          autohide: true,
          format: "yyyy-mm-dd",
          minDate: this.minDateStr,
          maxDate: this.maxDateStr,
        });
        input.addEventListener("changeDate", () => {
          this.updateCropBarCharts();
        });
        clearInterval(interval);
      }
    }, 100);

    fromEvent(window, "resize")
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(250))
      .subscribe(() => {
        this.updateChartHeights();
      });

    this.circleResizeSubject
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(150))
      .subscribe(() => this.updateCircleSize());

    this.circleResizeObserver = new ResizeObserver(() =>
      this.circleResizeSubject.next(),
    );
    this.circleResizeObserver.observe(this.cardsContainerRef.nativeElement);
  }

  updateCharts() {
    const dateList = this.totalETPrecipCumulativeET
      .map((point) => point.et_date)
      ?.slice(-this.timeRangeFilter[this.timeRange]);
    const etas = this.totalETPrecipCumulativeET
      .map((point) => point.eta)
      ?.slice(-this.timeRangeFilter[this.timeRange]);
    const precipitation = this.totalETPrecipCumulativeET
      .map((point) => (point.prcp < 0.02 ? null : point.prcp))
      ?.slice(-this.timeRangeFilter[this.timeRange]);
    const cetas = this.totalETPrecipCumulativeET
      .map((point) => point.ceta)
      ?.slice(-this.timeRangeFilter[this.timeRange]);
    const cprcp = this.totalETPrecipCumulativeET
      .map((point) => (point.cprcp < 0.02 ? null : point.cprcp))
      ?.slice(-this.timeRangeFilter[this.timeRange]);

    const updateOption = getUpdateTimeseriesChartConfig(
      precipitation,
      cprcp,
      etas,
      cetas,
      dateList,
    );
    this.chart?.updateOptions({
      ...updateOption,
      ...(dateList.length === 0 ? { series: [] } : {}),
      noData: { text: "ET data unavailable for this date range." },
    });
  }

  updateDonutCharts() {
    if (this.displayFieldsStatus) {
      const claimedFieldsCountNumber =
        this.claimedFieldsCount.toLocaleString() + " / ";
      this.summaryChart.updateOptions({
        series: [
          this.claimedFieldsCount,
          this.totalFieldsCount - this.claimedFieldsCount,
        ],
        colors: ["#E74694", "#1C64F2"],
        labels: ["Claimed Fields", "Unclaimed Fields"],
        plotOptions: {
          pie: {
            donut: {
              labels: {
                total: {
                  label: "Fields Claimed",
                  formatter: function (w: {
                    globals: { seriesTotals: number[] };
                  }) {
                    const sum = w.globals.seriesTotals.reduce((a, b) => a + b);
                    return (
                      claimedFieldsCountNumber + Number(sum).toLocaleString()
                    );
                  },
                },
              },
            },
          },
        },
        legend: {
          fontSize: this.getDonutLegendFontSize(),
        },
      });
    } else {
      const claimedAcresCountNumber =
        Math.round(this.totalClaimedAcres).toLocaleString() + " / ";
      this.summaryChart.updateOptions({
        series: [
          Math.round(this.totalClaimedAcres),
          Math.round(this.totalAcres) - Math.round(this.totalClaimedAcres),
        ],
        colors: ["#16BDCA", "#FDBA8C"],
        labels: ["Claimed Acres", "Unclaimed Acres"],
        plotOptions: {
          pie: {
            donut: {
              labels: {
                total: {
                  label: "Acres Claimed",
                  formatter: function (w: {
                    globals: { seriesTotals: number[] };
                  }) {
                    const sum = w.globals.seriesTotals.reduce((a, b) => a + b);
                    return (
                      claimedAcresCountNumber + Number(sum).toLocaleString()
                    );
                  },
                },
              },
            },
          },
        },
        legend: {
          fontSize: this.getDonutLegendFontSize(),
        },
      });
    }
  }

  updateCropBarCharts() {
    if (!this.dashboardData) {
      return;
    }
    const selectedDate =
      this.datepickerInputRef.nativeElement.value || this.maxDateStr;
    const cropSummary =
      this.dashboardData.cropSummariesByDate.find(
        (summary) => summary.date === selectedDate,
      ) ??
      [...this.dashboardData.cropSummariesByDate]
        .reverse()
        .find((summary) => summary.date <= selectedDate) ??
      this.dashboardData.cropSummariesByDate[0];

    if (!cropSummary) {
      this.cropCetaPerennialDict = {};
      this.cropTypesCount = 0;
      this.sortedCropCetaPerennialList = [];
      this.cropBarChart?.updateOptions({
        series: [{ name: "Cumulative Avg ET (ac-ft/ac)", data: [] }],
        labels: [],
      });
      return;
    }

    this.cropCetaPerennialDict = {};
    for (const crop of cropSummary.crops) {
      this.cropCetaPerennialDict[crop.crop] = [
        crop.cumulativeAvgEtAcFtPerAc,
        crop.acres,
        crop.fieldsCount,
        cropColorsLegend[crop.crop] ?? "#6B7280",
      ];
    }

    this.cropTypesCount = cropSummary.crops.length;
    this.sortedCropCetaPerennialList = Object.entries(
      this.cropCetaPerennialDict,
    ).sort((a, b) => b[1][1] - a[1][1]);
    const topFiveCrops = this.sortedCropCetaPerennialList.slice(0, 5);
    const labelsWithoutMis = topFiveCrops.map(([crop]) =>
      crop.replace(/^Miscellaneous\s+/, ""),
    );
    const series = topFiveCrops.map(([, summary]) => summary[0]);

    this.cropBarChart?.updateOptions({
      series: [
        {
          name: "Cumulative Avg ET (ac-ft/ac)",
          data: series,
        },
      ],
      colors: cropColorsLegendTopFive,
      labels: labelsWithoutMis,
      tooltip: {
        followCursor: true,
        intersect: false,
        style: {
          fontFamily: "Inter, sans-serif",
          colors: ["Red"],
        },
        y: {
          formatter: function (value: number) {
            return value.toLocaleString();
          },
        },
      },
      dataLabels: {
        style: {
          fontSize: this.getDonutLegendFontSize(),
        },
      },
    });
  }

  switchStatus(option: string) {
    this.displayFieldsStatus = option === "Fields";
    this.updateDonutCharts();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.chart = null;
    this.summaryChart = null;
    this.waterSourcesChart?.destroy();
    this.waterSourcesChart = undefined;
    this.cropBarChart = null;
    this.clickListener?.();
    this.circleResizeObserver?.disconnect();
    this.loadingService.stop("crop-bar-chart-animation");
  }

  private loadDashboardJson(data: ManagerDashboardJson) {
    this.dashboardData = {
      ...data,
      cropSummariesByDate: this.normalizeCropSummariesByDate(
        data.cropSummariesByDate,
      ),
    };
    this.title = data.title;
    this.dashboardUpdatedDate = this.formatDashboardUpdatedDate(
      data.updatedAt ?? data.updateAt,
    );
    this.growerCount = data.usersCount;
    this.claimedFieldsCount = data.claimedFieldsCount;
    this.totalFieldsCount = data.totalFieldsCount;
    this.totalClaimedAcres = data.totalClaimedAcres;
    this.totalAcres = data.totalAcres;
    this.totalETInFt = data.cumulativeAvgEtAcFtPerAc;
    this.totalETInInch = data.cumulativeAvgEtAcFtPerAc * 12;
    this.totalPrcpInInch = data.cumulativeAvgPrecipIn;
    this.claimedRate = (data.claimedFieldsCount / data.totalFieldsCount) * 100;
    this.projectedTotalConsumption =
      data.estimatedCumulativeTotalConsumptionTaf;
    this.totalETPrecipCumulativeET = data.timeseries;

    this.chart = new ApexCharts(
      this.chartElement.nativeElement,
      this.chartOptions,
    );
    this.chart.render();

    const summaryDonutChartOption = getSummaryChartOptions(this.mode);
    this.summaryChart = new ApexCharts(
      this.summaryChartElement.nativeElement,
      summaryDonutChartOption,
    );
    this.summaryChart.render();

    if (this.waterSourcesChartElement) {
      const totalGroundwater = Number(data.totalGroundWaterMed) || 0;
      const totalSurfaceWater = Number(data.totalSurfaceWaterAppliedMed) || 0;
      this.waterSourcesChart = new ApexCharts(
        this.waterSourcesChartElement.nativeElement,
        {
          series: [totalGroundwater, totalSurfaceWater],
          labels: ["Total Groundwater", "Total Surface Water"],
          colors: ["#1565C0", "#26A69A"],
          chart: {
            type: "donut",
            height: "100%",
            fontFamily: "Inter, sans-serif",
            foreColor: this.mode === "dark" ? "#f1f5f9" : "#374151",
            toolbar: { show: false },
          },
          dataLabels: { enabled: false },
          stroke: { width: 2, colors: ["transparent"] },
          plotOptions: {
            pie: {
              donut: {
                size: "80%",
                labels: {
                  show: true,
                  name: {
                    show: true,
                    fontFamily: "Inter, sans-serif",
                    fontSize: "1rem",
                    offsetY: 20,
                    color: this.mode === "dark" ? "#f1f5f9" : "#374151",
                  },
                  value: {
                    show: true,
                    fontFamily: "Inter, sans-serif",
                    fontSize: "1rem",
                    offsetY: -20,
                    color: this.mode === "dark" ? "#f1f5f9" : "#111827",
                  },
                  total: {
                    show: true,
                    showAlways: true,
                    label: "Total Water",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "1rem",
                    color: this.mode === "dark" ? "#f1f5f9" : "#374151",
                    formatter: () =>
                      (totalGroundwater + totalSurfaceWater).toLocaleString(
                        "en-US",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      ),
                  },
                },
              },
            },
          },
          legend: {
            show: true,
            position: "bottom",
            fontFamily: "Inter, sans-serif",
            fontSize: "12px",
            labels: {
              colors: this.mode === "dark" ? "#f1f5f9" : "#374151",
            },
            markers: { size: 5 },
            itemMargin: { horizontal: 4, vertical: 1 },
          },
          tooltip: {
            y: {
              formatter: (value: number) =>
                value.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
            },
          },
        },
      );
      this.waterSourcesChart.render();
    }

    if (this.cropBarChartElement) {
      this.loadingService.start({
        token: "crop-bar-chart-animation",
      });
      const cropBarChartOption = getCropBarChartOptions(this.mode, () => {
        this.loadingService.stop("crop-bar-chart-animation");
      });
      this.cropBarChart = new ApexCharts(
        this.cropBarChartElement.nativeElement,
        cropBarChartOption,
      );
      this.cropBarChart.render();
    }
    this.updateCharts();
    this.updateDonutCharts();
    this.updateCropBarCharts();
    this.updateCircleSize();
  }

  private fetchManagerStatisticsSummary() {
    this.loadingService.start({
      token: "manager-statistics-summary",
    });
    this.dataService
      .getManagerStatisticsSummary()
      .pipe(
        finalize(() => this.loadingService.stop("manager-statistics-summary")),
        catchError((err) => {
          console.error("Cannot get manager statistics summary", err);
          return EMPTY;
        }),
      )
      .subscribe((summary: ManagerDashboardJson) => {
        this.loadDashboardJson(summary);
      });
  }

  private getDonutLegendFontSize() {
    return window.innerHeight < 1024 ? "12px" : "14px";
  }

  private normalizeCropLabel(crop: string): string {
    return crop
      .split("||")
      .map((cropPart) => cropDict[cropPart.trim()] ?? cropPart.trim())
      .filter(Boolean)
      .join("-");
  }

  private normalizeCropSummariesByDate(
    summariesByDate: DashboardCropSummaryByDate[],
  ): DashboardCropSummaryByDate[] {
    return summariesByDate.map((summaryByDate) => {
      const cropSummaries = new Map<string, DashboardCropSummaryAccumulator>();
      for (const cropSummary of summaryByDate.crops) {
        const crop = this.normalizeCropLabel(cropSummary.crop);
        const current = cropSummaries.get(crop) ?? {
          acres: 0,
          fieldsCount: 0,
          cumulativeEtAcres: 0,
        };
        current.acres += cropSummary.acres;
        current.fieldsCount += cropSummary.fieldsCount;
        current.cumulativeEtAcres +=
          cropSummary.cumulativeAvgEtAcFtPerAc * cropSummary.acres;
        cropSummaries.set(crop, current);
      }
      return {
        date: summaryByDate.date,
        crops: Array.from(cropSummaries.entries())
          .map(([crop, summary]) => ({
            crop,
            acres: Number(summary.acres.toFixed(2)),
            fieldsCount: summary.fieldsCount,
            cumulativeAvgEtAcFtPerAc:
              summary.acres > 0 ?
                Number((summary.cumulativeEtAcres / summary.acres).toFixed(1))
              : 0,
          }))
          .sort((a, b) => b.acres - a.acres),
      };
    });
  }

  private updateChartHeights(): void {
    if (this.summaryChart) {
      this.updateDonutCharts();
    }
    if (this.cropBarChart) {
      this.updateCropBarCharts();
    }
  }

  private updateCircleSize() {
    const cardsContainer = this.cardsContainerRef?.nativeElement;
    if (!cardsContainer) {
      return;
    }
    const cardWidth = (cardsContainer.clientWidth - 24) / 2;
    const cardHeight = (cardsContainer.clientHeight - 24) / 2;
    const sizeFromWidth = cardWidth * 0.48;
    const sizeFromHeight = cardHeight - 42;
    const nextSize = Math.max(56, Math.min(sizeFromWidth, sizeFromHeight, 104));
    this.circleSize = Math.round(nextSize);
    this.cdr.detectChanges();
  }
}
